import React from "react";
import { useParams } from "react-router-dom";
import ActionsMenu from "../../../../../components/ActionsMenu";
import {
  DownloadMenuProps,
  ExportStatusType
} from "../../../@types/rldynamicanalysis";
import {
  useDownloadDroppedFiles,
  useDroppedFilesStaticAnalysis,
  useGetRLSandboxResourcesStatus
} from "../../../hooks";
import { DROPPED_FILES_ACTIONS } from "../consts";
import {
  createDroppedFilesActionDisabledMessage,
  getDownloadDroppedFilesDisabledMessage
} from "../utils";
import {
  useRlSandboxHtmlSummaryExport,
  useRlSandboxPdfSummaryExport
} from "./hooks";

const getButtonLabelFor = (type: "PDF" | "HTML", status: ExportStatusType) => {
  const { DOWNLOAD, WAIT } = window.tcbase.consts.pdf_action;

  switch (status) {
    case DOWNLOAD:
      return `Download ${type}`;
    case WAIT:
      return `Creating ${type}`;
    default:
      return `Create ${type}`;
  }
};

const isButtonDisabled = (exportStatus: ExportStatusType) =>
  exportStatus == null || exportStatus === window.tcbase.consts.pdf_action.WAIT;

const DownloadMenu: React.FC<DownloadMenuProps> = ({
  analysisId,
  hash,
  isPrivate,
  isStoredReport,
  isNetworkReport = false
}) => {
  const { sourceId } = useParams();

  const { pdfExportStatus, downloadOrRequestPdfCreation } =
    useRlSandboxPdfSummaryExport(hash);

  const { htmlExportStatus, downloadOrRequestHtmlCreation } =
    useRlSandboxHtmlSummaryExport(hash);

  const { downloadDroppedFiles } = useDownloadDroppedFiles(
    hash,
    analysisId,
    isNetworkReport
  );
  const { data } = useGetRLSandboxResourcesStatus(
    hash,
    analysisId,
    isNetworkReport,
    {
      enabled: Boolean(analysisId)
    }
  );
  const { sendToStaticAnalysis, isLoading } = useDroppedFilesStaticAnalysis(
    hash,
    analysisId
  );

  const disabledMessage = createDroppedFilesActionDisabledMessage(
    isStoredReport,
    data?.has_dropped_files
  );

  const sendToStaticAnalysisDisabledMsg =
    getDownloadDroppedFilesDisabledMessage(
      isStoredReport,
      data?.has_dropped_files,
      isPrivate,
      isLoading,
      sourceId
    );

  const actions = [
    {
      label: getButtonLabelFor("PDF", pdfExportStatus),
      onClick: downloadOrRequestPdfCreation,
      disabled: isButtonDisabled(pdfExportStatus)
    },
    {
      label: getButtonLabelFor("HTML", htmlExportStatus),
      onClick: downloadOrRequestHtmlCreation,
      disabled: isButtonDisabled(htmlExportStatus)
    },
    {
      label: DROPPED_FILES_ACTIONS.DOWNLOAD_LATEST,
      onClick: downloadDroppedFiles,
      disabled: isStoredReport || !data?.has_dropped_files,
      disabledMessage
    },
    {
      label: DROPPED_FILES_ACTIONS.SEND_LATEST_TO_STATIC_ANALYSIS,
      onClick: sendToStaticAnalysis,
      disabled:
        isStoredReport || !data?.has_dropped_files || isLoading || !sourceId,
      disabledMessage: sendToStaticAnalysisDisabledMsg
    }
  ];

  return (
    <ActionsMenu
      id={`rl-cloud-sandbox-actions-${hash}`}
      buttonClassName="summary-header__button"
      actions={actions}
      title="Actions"
      showDropdownIcon
    />
  );
};

export default DownloadMenu;
